<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { api, type MenuDocument, type MenuItem, type MenuSection } from "./api";

const props = defineProps<{ siteId: string }>();
const menu = ref<MenuDocument | null>(null);
const canEdit = ref(false);
const loading = ref(true);
const saving = ref(false);
const error = ref("");
const notice = ref("");
const selectedSectionIndex = ref(0);
const expandedItemIndex = ref<number | null>(null);
const itemSearch = ref("");
const draggedSectionIndex = ref<number | null>(null);
const sectionDropIndex = ref<number | null>(null);

const activeSection = computed(() => menu.value?.sections[selectedSectionIndex.value] ?? null);
const totalItems = computed(() =>
  menu.value?.sections.reduce((total, section) => total + section.items.length, 0) ?? 0,
);
const visibleItems = computed(() => {
  const section = activeSection.value;
  const query = itemSearch.value.trim().toLocaleLowerCase();
  if (!section) return [];
  return section.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) =>
      !query || `${item.name} ${item.description}`.toLocaleLowerCase().includes(query),
    );
});

function newItem(): MenuItem {
  return { name: "", description: "", price: "0.00", calories: null, isAvailable: true };
}

function newSection(): MenuSection {
  return { name: "New section", description: "", items: [] };
}

function move<T>(items: T[], index: number, direction: -1 | 1) {
  const destination = index + direction;
  if (destination < 0 || destination >= items.length) return false;
  const [item] = items.splice(index, 1);
  if (item !== undefined) items.splice(destination, 0, item);
  return true;
}

function selectSection(index: number) {
  selectedSectionIndex.value = index;
  expandedItemIndex.value = null;
  itemSearch.value = "";
}

function addSection() {
  if (!menu.value) return;
  menu.value.sections.push(newSection());
  selectSection(menu.value.sections.length - 1);
}

function moveActiveSection(direction: -1 | 1) {
  if (!menu.value || !move(menu.value.sections, selectedSectionIndex.value, direction)) return;
  selectedSectionIndex.value += direction;
}

function startSectionDrag(event: DragEvent, index: number) {
  if (!canEdit.value) {
    event.preventDefault();
    return;
  }
  draggedSectionIndex.value = index;
  sectionDropIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  }
}

function dragOverSection(event: DragEvent, index: number) {
  if (draggedSectionIndex.value === null) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
  sectionDropIndex.value = index;
}

function dropSection(event: DragEvent, targetIndex: number) {
  event.preventDefault();
  const sourceIndex = draggedSectionIndex.value;
  if (!menu.value || sourceIndex === null || sourceIndex === targetIndex) {
    endSectionDrag();
    return;
  }

  const selectedSection = activeSection.value;
  const [section] = menu.value.sections.splice(sourceIndex, 1);
  if (section) menu.value.sections.splice(targetIndex, 0, section);
  if (selectedSection) selectedSectionIndex.value = menu.value.sections.indexOf(selectedSection);
  endSectionDrag();
}

function endSectionDrag() {
  draggedSectionIndex.value = null;
  sectionDropIndex.value = null;
}

function removeActiveSection() {
  if (!menu.value || !activeSection.value) return;
  menu.value.sections.splice(selectedSectionIndex.value, 1);
  selectedSectionIndex.value = Math.min(
    selectedSectionIndex.value,
    Math.max(menu.value.sections.length - 1, 0),
  );
  expandedItemIndex.value = null;
  itemSearch.value = "";
}

function addItem() {
  if (!activeSection.value) return;
  itemSearch.value = "";
  activeSection.value.items.push(newItem());
  expandedItemIndex.value = activeSection.value.items.length - 1;
}

function toggleItem(index: number) {
  expandedItemIndex.value = expandedItemIndex.value === index ? null : index;
}

function moveItem(index: number, direction: -1 | 1) {
  if (!activeSection.value || !move(activeSection.value.items, index, direction)) return;
  if (expandedItemIndex.value === index) expandedItemIndex.value = index + direction;
  else if (expandedItemIndex.value === index + direction) expandedItemIndex.value = index;
}

function removeItem(index: number) {
  if (!activeSection.value) return;
  activeSection.value.items.splice(index, 1);
  if (expandedItemIndex.value === index) expandedItemIndex.value = null;
  else if (expandedItemIndex.value !== null && expandedItemIndex.value > index) {
    expandedItemIndex.value -= 1;
  }
}

async function save() {
  if (!menu.value || !canEdit.value) return;
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    menu.value = (await api.updateMenu(props.siteId, menu.value)).menu;
    notice.value = "Menu saved and available to the website.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save the menu";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  try {
    const response = await api.menu(props.siteId);
    menu.value = response.menu;
    canEdit.value = response.canEdit;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load the menu";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="menu-card">
    <div class="module-heading">
      <div>
        <p class="eyebrow">Food &amp; drink module</p>
        <h2>Menu</h2>
        <p>Choose a section, then expand only the item you want to edit.</p>
      </div>
      <button v-if="menu" type="button" :disabled="saving || !canEdit" @click="save">
        {{ saving ? "Saving..." : "Save menu" }}
      </button>
    </div>

    <p v-if="error" class="message error" role="alert">{{ error }}</p>
    <p v-if="notice" class="message success" role="status">{{ notice }}</p>
    <p v-if="loading" class="loading">Loading menu...</p>

    <template v-else-if="menu">
      <div class="menu-settings">
        <label>Menu title<input v-model="menu.title" :disabled="!canEdit" maxlength="120"></label>
        <label>
          Currency
          <select v-model="menu.currency" :disabled="!canEdit">
            <option value="GBP">GBP (&pound;)</option>
            <option value="EUR">EUR (&euro;)</option>
            <option value="USD">USD ($)</option>
          </select>
        </label>
      </div>

      <div class="menu-workspace">
        <div class="section-panel">
          <div class="panel-heading">
            <div>
              <strong>Sections</strong>
              <span>{{ menu.sections.length }} total</span>
            </div>
            <button class="compact-add" type="button" :disabled="!canEdit" @click="addSection" aria-label="Add menu section">+</button>
          </div>

          <div v-if="menu.sections.length" class="section-nav" role="tablist" aria-label="Menu sections">
            <div
              v-for="(section, sectionIndex) in menu.sections"
              :key="section.id || sectionIndex"
              class="section-tab-row"
              :class="{
                active: selectedSectionIndex === sectionIndex,
                dragging: draggedSectionIndex === sectionIndex,
                'drop-target': sectionDropIndex === sectionIndex && draggedSectionIndex !== sectionIndex,
              }"
              @dragover="dragOverSection($event, sectionIndex)"
              @drop="dropSection($event, sectionIndex)"
            >
              <span
                class="drag-handle"
                :class="{ disabled: !canEdit }"
                :draggable="canEdit"
                role="button"
                :tabindex="canEdit ? 0 : -1"
                :aria-label="`Drag to reorder ${section.name || 'section'}`"
                title="Drag to reorder"
                @dragstart.stop="startSectionDrag($event, sectionIndex)"
                @dragend="endSectionDrag"
              >
                <span class="sr-only">Drag to reorder</span>
              </span>
              <button
                class="section-tab"
                type="button"
                role="tab"
                :aria-selected="selectedSectionIndex === sectionIndex"
                @click="selectSection(sectionIndex)"
              >
                <span class="section-position">{{ String(sectionIndex + 1).padStart(2, "0") }}</span>
                <span class="section-summary">
                  <strong>{{ section.name || "Untitled section" }}</strong>
                  <small>{{ section.items.length }} {{ section.items.length === 1 ? "item" : "items" }}</small>
                </span>
                <span v-if="section.items.some((item) => !item.isAvailable)" class="availability-dot" title="Contains unavailable items"></span>
              </button>
            </div>
          </div>

          <div v-else class="section-empty">
            <p>No sections yet.</p>
            <button type="button" :disabled="!canEdit" @click="addSection">Add first section</button>
          </div>
        </div>

        <article v-if="activeSection" class="section-editor">
          <div class="section-toolbar">
            <div>
              <p class="eyebrow">Section {{ selectedSectionIndex + 1 }}</p>
              <h3>{{ activeSection.name || "Untitled section" }}</h3>
            </div>
            <div class="toolbar-actions">
              <button class="mini" type="button" :disabled="!canEdit || selectedSectionIndex === 0" aria-label="Move section up" @click="moveActiveSection(-1)">&uarr;</button>
              <button class="mini" type="button" :disabled="!canEdit || selectedSectionIndex === menu.sections.length - 1" aria-label="Move section down" @click="moveActiveSection(1)">&darr;</button>
              <button class="remove" type="button" :disabled="!canEdit" @click="removeActiveSection">Remove section</button>
            </div>
          </div>

          <div class="section-fields">
            <label>Section name<input v-model="activeSection.name" :disabled="!canEdit" maxlength="120" placeholder="Main courses"></label>
            <label>Section description<input v-model="activeSection.description" :disabled="!canEdit" maxlength="500" placeholder="Optional introduction"></label>
          </div>

          <div class="item-list-heading">
            <div>
              <strong>Items</strong>
              <span>{{ activeSection.items.length }} in this section</span>
            </div>
            <label class="item-search">
              <span class="sr-only">Search this section</span>
              <input v-model="itemSearch" type="search" placeholder="Search this section...">
            </label>
          </div>

          <div v-if="visibleItems.length" class="items">
            <article
              v-for="entry in visibleItems"
              :key="entry.item.id || entry.index"
              class="menu-item"
              :class="{ expanded: expandedItemIndex === entry.index }"
            >
              <div class="item-row">
                <button
                  class="item-toggle"
                  type="button"
                  :aria-expanded="expandedItemIndex === entry.index"
                  @click="toggleItem(entry.index)"
                >
                  <span class="disclosure">{{ expandedItemIndex === entry.index ? "-" : "+" }}</span>
                  <span class="item-summary">
                    <strong>{{ entry.item.name || `Untitled item ${entry.index + 1}` }}</strong>
                    <small v-if="entry.item.description">{{ entry.item.description }}</small>
                    <small v-else>No description</small>
                  </span>
                  <span class="item-meta">
                    <span class="price">{{ menu.currencySymbol }}{{ entry.item.price || "0.00" }}</span>
                    <span v-if="entry.item.calories !== null" class="calories">{{ entry.item.calories }} kcal</span>
                    <span class="status-pill" :class="{ unavailable: !entry.item.isAvailable }">
                      {{ entry.item.isAvailable ? "Available" : "Unavailable" }}
                    </span>
                  </span>
                </button>
                <div class="item-actions">
                  <button class="mini" type="button" :disabled="!canEdit || entry.index === 0" aria-label="Move item up" @click="moveItem(entry.index, -1)">&uarr;</button>
                  <button class="mini" type="button" :disabled="!canEdit || entry.index === activeSection.items.length - 1" aria-label="Move item down" @click="moveItem(entry.index, 1)">&darr;</button>
                  <button class="remove icon-remove" type="button" :disabled="!canEdit" aria-label="Remove item" @click="removeItem(entry.index)">&times;</button>
                </div>
              </div>

              <div v-if="expandedItemIndex === entry.index" class="item-fields">
                <label class="item-name">Name<input v-model="entry.item.name" :disabled="!canEdit" maxlength="160" placeholder="Menu item name"></label>
                <label>Price ({{ menu.currencySymbol }})<input v-model="entry.item.price" :disabled="!canEdit" inputmode="decimal" placeholder="0.00"></label>
                <label>Calories<input v-model.number="entry.item.calories" :disabled="!canEdit" type="number" min="0" max="20000" placeholder="Optional"></label>
                <label class="item-description">Description<textarea v-model="entry.item.description" :disabled="!canEdit" rows="3" maxlength="1000" placeholder="Ingredients or a short description"></textarea></label>
                <label class="available"><input v-model="entry.item.isAvailable" :disabled="!canEdit" type="checkbox"> Currently available</label>
              </div>
            </article>
          </div>

          <div v-else-if="itemSearch" class="items-empty">
            <p>No items match “{{ itemSearch }}”.</p>
            <button class="quiet-action" type="button" @click="itemSearch = ''">Clear search</button>
          </div>
          <div v-else class="items-empty"><p>This section has no items yet.</p></div>

          <button class="add-item" type="button" :disabled="!canEdit" @click="addItem">+ Add item to {{ activeSection.name || "section" }}</button>
        </article>
      </div>

      <div class="module-save">
        <span>{{ menu.sections.length }} sections &middot; {{ totalItems }} items</span>
        <button type="button" :disabled="saving || !canEdit" @click="save">{{ saving ? "Saving..." : "Save menu" }}</button>
      </div>
    </template>
  </section>
</template>

<style scoped>
.menu-card { display: grid; gap: 1.25rem; padding: 1.5rem; border: 1px solid #d9ddd8; border-radius: 1rem; background: #fff; }
.module-heading, .section-toolbar, .module-save, .panel-heading, .item-list-heading { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.module-heading h2, .section-toolbar h3 { margin: 0; }
.module-heading p:last-child { margin: .35rem 0 0; color: #6d7972; }
.eyebrow { margin: 0 0 .25rem; color: #6b7b72; font-size: .68rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.menu-settings, .section-fields { display: grid; gap: .8rem; grid-template-columns: 2fr 1fr; }
label { display: grid; gap: .4rem; }
select, textarea { width: 100%; padding: .75rem; border: 1px solid #cbd5ce; border-radius: .65rem; background: white; font: inherit; }
.menu-workspace { display: grid; align-items: start; gap: 1rem; grid-template-columns: minmax(13rem, 17rem) minmax(0, 1fr); }
.section-panel, .section-editor { border: 1px solid #dce2dc; border-radius: .85rem; background: #fafbf9; }
.section-panel { position: sticky; top: 1rem; overflow: hidden; }
.panel-heading { padding: 1rem; border-bottom: 1px solid #dfe5df; }
.panel-heading > div, .item-list-heading > div { display: grid; gap: .12rem; }
.panel-heading span, .item-list-heading span { color: #718078; font-size: .72rem; }
.compact-add { display: grid; width: 2.15rem; height: 2.15rem; padding: 0; place-items: center; font-size: 1.2rem; }
.section-nav { display: grid; max-height: 64vh; overflow-y: auto; padding: .5rem; gap: .3rem; scrollbar-width: auto; scrollbar-color: #789286 #edf1ed; }
.section-tab-row { display: grid; position: relative; grid-template-columns: 1.3rem minmax(0, 1fr); align-items: stretch; border: 1px solid transparent; border-radius: .68rem; background: transparent; transition: border-color .15s, background .15s, box-shadow .15s, opacity .15s; }
.section-tab-row:hover { background: #eef2ee; }
.section-tab-row.active { color: white; background: #285c47; box-shadow: 0 .35rem .9rem rgb(31 70 54 / 16%); }
.section-tab-row.dragging { opacity: .42; }
.section-tab-row.drop-target { border-color: #d4633a; box-shadow: inset 0 0 0 1px #d4633a; }
.drag-handle { display: grid; width: 100%; place-items: center; cursor: grab; touch-action: none; }
.drag-handle:active { cursor: grabbing; }
.drag-handle::before { width: .55rem; height: 1.25rem; content: ""; opacity: .55; background-image: radial-gradient(circle, currentColor 1.35px, transparent 1.6px); background-position: 0 0; background-size: .28rem .28rem; }
.drag-handle.disabled { cursor: default; opacity: .3; }
.section-tab { display: grid; width: 100%; padding: .75rem .75rem .75rem .25rem; grid-template-columns: 1.75rem minmax(0, 1fr) auto; align-items: center; gap: .6rem; color: #31473d; background: transparent; text-align: left; }
.section-tab-row.active .section-tab { color: white; }
.section-position { font-size: .66rem; font-weight: 800; opacity: .65; }
.section-summary { display: grid; min-width: 0; gap: .15rem; }
.section-summary strong { overflow: hidden; font-size: .84rem; text-overflow: ellipsis; white-space: nowrap; }
.section-summary small { font-size: .68rem; opacity: .72; }
.availability-dot { width: .45rem; height: .45rem; border-radius: 50%; background: #cf7745; }
.section-tab-row.active .availability-dot { background: #ffd39e; }
.section-empty { padding: 1rem; text-align: center; }
.section-empty p, .items-empty p { margin: 0; color: #718078; }
.section-editor { min-width: 0; padding: 1.2rem; }
.section-toolbar { margin-bottom: 1rem; }
.toolbar-actions, .item-actions { display: flex; flex: 0 0 auto; gap: .4rem; }
.mini { min-width: 2rem; padding: .35rem; color: #365045; background: #edf1ed; }
.remove { padding: .4rem .65rem; color: #91382f; background: #fae9e7; }
.icon-remove { min-width: 2rem; padding-inline: .45rem; font-size: 1rem; }
.item-list-heading { margin-top: 1.3rem; padding-top: 1.1rem; border-top: 1px solid #e0e5e0; }
.item-search { width: min(100%, 16rem); }
.item-search input { padding: .58rem .7rem; font-size: .8rem; }
.items { display: grid; margin-top: .8rem; gap: .5rem; }
.menu-item { overflow: hidden; border: 1px solid #dfe4df; border-radius: .72rem; background: white; transition: border-color .15s, box-shadow .15s; }
.menu-item.expanded { border-color: #85a394; box-shadow: 0 .35rem 1.2rem rgb(34 73 57 / 8%); }
.item-row { display: flex; align-items: center; gap: .5rem; }
.item-toggle { display: grid; min-width: 0; padding: .8rem; flex: 1; grid-template-columns: 1.5rem minmax(0, 1fr) auto; align-items: center; gap: .65rem; color: #24382f; background: transparent; text-align: left; }
.item-toggle:hover { background: #f6f8f6; }
.disclosure { display: grid; width: 1.45rem; height: 1.45rem; place-items: center; border-radius: 50%; color: #285c47; background: #e8f0eb; font-size: 1rem; }
.item-summary { display: grid; min-width: 0; gap: .15rem; }
.item-summary strong, .item-summary small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.item-summary strong { font-size: .86rem; }
.item-summary small { color: #738078; font-size: .72rem; font-weight: 500; }
.item-meta { display: flex; align-items: center; justify-content: flex-end; gap: .5rem; }
.price { min-width: 3.5rem; font-size: .82rem; font-weight: 800; text-align: right; }
.calories { color: #718078; font-size: .68rem; }
.status-pill { padding: .25rem .45rem; border-radius: 999px; color: #2f6548; background: #e5f2e9; font-size: .64rem; font-weight: 800; }
.status-pill.unavailable { color: #8c493a; background: #fae9e5; }
.item-fields { display: grid; padding: 1rem; border-top: 1px solid #e2e7e2; gap: .8rem; grid-template-columns: 2fr .65fr .65fr; background: #fbfcfb; }
.item-description { grid-column: span 2; }
.available { display: flex; align-items: center; gap: .5rem; }
.available input { width: auto; }
.items-empty { display: flex; min-height: 6rem; margin-top: .8rem; align-items: center; justify-content: center; flex-direction: column; gap: .7rem; border: 1px dashed #cbd5ce; border-radius: .72rem; }
.quiet-action { padding: .35rem .6rem; color: #285c47; background: #e8f0eb; }
.add-item { width: 100%; margin-top: .8rem; border: 1px dashed #9aaca1; color: #315342; background: transparent; }
.module-save { padding-top: 1rem; border-top: 1px solid #e1e5e1; color: #6d7972; font-size: .8rem; }
.message { margin: 0; padding: .85rem 1rem; border-radius: .65rem; }
.message.error { color: #86251f; background: #fee9e7; }
.message.success { color: #295b40; background: #e2f1e7; }
.loading { padding: 2rem; text-align: center; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
@media (max-width: 900px) {
  .menu-workspace { grid-template-columns: 1fr; }
  .section-panel { position: static; }
  .section-nav { display: flex; max-height: none; overflow-x: auto; overflow-y: hidden; }
  .section-tab-row { min-width: 12rem; }
}
@media (max-width: 700px) {
  .module-heading, .module-save, .section-toolbar, .item-list-heading { align-items: stretch; flex-direction: column; }
  .menu-settings, .section-fields, .item-fields { grid-template-columns: 1fr; }
  .item-description { grid-column: auto; }
  .toolbar-actions { align-self: flex-end; }
  .item-row { align-items: stretch; }
  .item-toggle { grid-template-columns: 1.5rem minmax(0, 1fr); }
  .item-meta { grid-column: 2; justify-content: flex-start; flex-wrap: wrap; }
  .item-actions { padding: .6rem .6rem .6rem 0; flex-direction: column; }
  .item-search { width: 100%; }
}
</style>
